/*
  Compact phone product page (assets/sivana-pdp-compact.css). Loaded only when "Compact phone
  layout" is on (sections/sivana-pdp-breadcrumb.liquid).
    1. Perk rows: "Learn more" opens its pop-up (blocks/sivana-pdp-perks.liquid); the X or a tap
       outside closes it.
    2. Gallery: previous/next arrows on the main photo (phones), driving Horizon's slideshow.
    3. "Find my fit" pill on the main photo (phones): fades up in the lower right once the shopper
       reaches the second photo (hides again on the first), and opens the size chart. Only when
       the product has a size chart (snippets/sivana-size-guide.liquid).
    4. Color swatches (the swatch app's row, .pl-swatches__container) on phones, when there are more
       colors than fit: the row starts with the current color in view, does one gentle slide the
       first time it comes on screen (and settles back) so shoppers see it scrolls, and fades only
       the side(s) that still have colors hidden.
*/
(() => {
  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const opener = target.closest('[data-sivana-perk-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-sivana-perk-open'));
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
      return;
    }
    if (target.closest('[data-sivana-perk-close]')) {
      target.closest('dialog')?.close();
      return;
    }
    // A tap on the dimmed backdrop lands on the dialog element itself
    if (target instanceof HTMLDialogElement && target.classList.contains('sivana-perk-dialog')) {
      target.close();
    }
  });

  const phone = window.matchMedia('(max-width: 749px)');
  const chevron = (d) =>
    `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="${d}" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  function addArrows() {
    if (!phone.matches) return;
    const container = document.querySelector('.product-information__media media-gallery slideshow-container');
    const slideshow = container?.closest('slideshow-component');
    if (!container || !slideshow || container.querySelector(':scope > .sivana-gallery-arrow')) return;
    for (const dir of ['previous', 'next']) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `sivana-gallery-arrow sivana-gallery-arrow--${dir}`;
      button.setAttribute('aria-label', dir === 'next' ? 'Next photo' : 'Previous photo');
      button.innerHTML = chevron(dir === 'next' ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7');
      button.addEventListener('click', (event) => {
        const show = /** @type {any} */ (container.closest('slideshow-component'));
        show?.[dir]?.(event);
      });
      container.append(button);
    }
  }

  const RULER =
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true"><rect x="2.5" y="8" width="19" height="8" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M6.5 8v3M10 8v4.2M13.5 8v3M17 8v4.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

  function addFitPill() {
    if (!phone.matches) return;
    const container = document.querySelector('.product-information__media media-gallery slideshow-container');
    const opener = document.querySelector('.product-information [data-sivana-size-open]');
    if (!container || !opener || container.querySelector(':scope > .sivana-fit-pill')) return;
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'sivana-fit-pill';
    pill.innerHTML = `${RULER}<span>Find my fit</span>`;
    pill.tabIndex = -1;
    pill.addEventListener('click', () => opener.click());
    container.append(pill);
  }

  // Show from the second photo on (any way the photo changes: swipe, arrows, thumbnails)
  document.addEventListener('slideshow:select', (event) => {
    const gallery = /** @type {HTMLElement} */ (event.target).closest?.('.product-information__media');
    const pill = gallery?.querySelector('.sivana-fit-pill');
    if (!pill) return;
    const on = (/** @type {any} */ (event).detail?.index || 0) >= 1;
    pill.classList.toggle('is-on', on);
    pill.tabIndex = on ? 0 : -1;
  });

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const glide = (row, from, to, ms) =>
    new Promise((resolve) => {
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / ms);
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        row.scrollLeft = from + (to - from) * eased;
        if (t < 1 && !row.sivanaTouched) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
      setTimeout(resolve, ms + 200); // in case frames are paused
    });

  function setupSwatchRow(row) {
    if (row.dataset.sivanaHint) return;
    row.dataset.sivanaHint = 'ready';
    const overflows = () => row.scrollWidth > row.clientWidth + 8;
    // Fade only the edge that still hides colors
    const fade = () => {
      if (!overflows()) {
        row.style.removeProperty('mask-image');
        row.style.removeProperty('-webkit-mask-image');
        return;
      }
      const left = row.scrollLeft > 4;
      const right = row.scrollLeft + row.clientWidth < row.scrollWidth - 4;
      const mask = `linear-gradient(90deg, ${left ? 'transparent 0, #000 12%' : '#000 0'}, ${right ? '#000 88%, transparent 100%' : '#000 100%'})`;
      row.style.setProperty('mask-image', mask);
      row.style.setProperty('-webkit-mask-image', mask);
    };
    row.addEventListener('scroll', fade, { passive: true });
    const stop = () => (row.sivanaTouched = true);
    row.addEventListener('pointerdown', stop, { passive: true });
    row.addEventListener('touchstart', stop, { passive: true });
    if (!overflows()) return;

    // Start with the current color in view
    const current = row.querySelector('[aria-current]');
    if (current) {
      const left = current.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
      if (left + current.offsetWidth > row.scrollLeft + row.clientWidth || left < row.scrollLeft) {
        row.scrollLeft = left - (row.clientWidth - current.offsetWidth) / 2;
      }
    }
    fade();
    if (reduceMotion.matches) return;

    // One gentle slide toward the hidden colors the first time the row is on screen
    const watcher = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        watcher.disconnect();
        setTimeout(async () => {
          if (row.sivanaTouched || !overflows()) return;
          const base = row.scrollLeft;
          const room = row.scrollWidth - row.clientWidth - base;
          const dist = room > 8 ? Math.min(64, room) : -Math.min(64, base);
          if (!dist) return;
          await glide(row, base, base + dist, 520);
          await new Promise((resolve) => setTimeout(resolve, 180));
          if (!row.sivanaTouched) await glide(row, row.scrollLeft, base, 560);
          fade();
        }, 450);
      },
      { threshold: 0.9 }
    );
    watcher.observe(row);
  }

  // The swatch app draws its row after the page loads; wait for it
  function watchSwatches() {
    if (!phone.matches) return;
    const find = () => document.querySelector('.product-information .pl-swatches__container');
    const row = find();
    if (row) return setupSwatchRow(row);
    const observer = new MutationObserver(() => {
      const found = find();
      if (found) {
        observer.disconnect();
        setupSwatchRow(found);
      }
    });
    observer.observe(document.querySelector('.product-information') || document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 15000);
  }

  const setup = () => {
    addArrows();
    addFitPill();
    watchSwatches();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
  phone.addEventListener('change', setup);
  // Horizon re-renders the gallery when the variant changes; put the arrows and pill back
  document.addEventListener('variant:update', () => requestAnimationFrame(setup));
})();
